import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { UntypedFormControl } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Component({
    selector: 'pv-shared-mat-chips',
    templateUrl: './shared-mat-chips.component.html',
    styleUrls: ['./shared-mat-chips.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SharedMatChipsComponent),
            multi: true
        }
    ],
    standalone: false
})
export class SharedMatChipsComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Input() label: string = 'Tags';
  @Input() placeholder: string = '';
  @Input() removable: boolean = true;
  @Input() selectable: boolean = true;
  @Input() autocompleteItems: any[] = [];
  @Input() displayWith: (item: any) => string = (item) => item;
  @Input() onlyFromAutocomplete: boolean = false;
  @Output() tagRemoved = new EventEmitter<any>();
  @Output() valueChanged = new EventEmitter<any[]>();

  separatorKeysCodes: number[] = [ENTER, COMMA];
  tags: any[] = [];
  inputCtrl = new UntypedFormControl();
  filteredItems: Observable<any[]>;
  hasAutocomplete: boolean = false;

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnInit() {
    this.filteredItems = this.inputCtrl.valueChanges.pipe(
      startWith(''),
      map(value => this._filter(value || ''))
    );
    this.hasAutocomplete = this.autocompleteItems?.length > 0;
    this.inputCtrl.valueChanges.subscribe(() => {
      this.onTouched();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.autocompleteItems) {
      this.hasAutocomplete = this.autocompleteItems?.length > 0;
    }
  }

  writeValue(value: any[]): void {
    this.tags = value || [];
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.inputCtrl.disable();
    } else {
      this.inputCtrl.enable();
    }
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    const selectedItem = event.option.value;
    if (!this.tags.includes(selectedItem)) {
      this.tags.push(selectedItem);
      this.emitChange();
    }
    this.inputCtrl.setValue('');
  }

  add(event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value;

    if ((value || '').trim()) {
      const item = this.autocompleteItems.find(i => this.displayWith(i) === value.trim());
      const newTag = item || value.trim();
      if (!this.tags.includes(newTag)) {
        this.tags.push(newTag);
        this.emitChange();
      }
    }

    if (input) {
      input.value = '';
    }
    this.inputCtrl.setValue('');
  }

  addFromInput(): void {
    const value = this.inputCtrl.value?.trim();
    if (value && !this.tags.includes(value)) {
      this.tags.push(value);
      this.emitChange();
      this.inputCtrl.setValue('');
    }
  }

  remove(tag: any): void {
    const index = this.tags.indexOf(tag);
    if (index >= 0) {
      this.tags.splice(index, 1);
      this.emitChange();
      this.tagRemoved.emit(tag);
    }
  }

  private emitChange(): void {
    this.onChange([...this.tags]);
    this.valueChanged.emit([...this.tags]);
  }

  private _filter(value: string): any[] {
    const filterValue = value.toLowerCase();
    return this.autocompleteItems.filter(item => this.displayWith(item).toLowerCase().includes(filterValue));
  }
}
